import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Layout, Radio } from 'antd';
import { useContext } from 'react';

import { SDetailLayout } from 'app/components/StaycationUI';
import { CurrencyInput } from 'app/components/commons/Currency/CurrencyInput/CurrencyInput';
import {
  CustomRow,
  DatePickerRow,
  InputNumberRow,
  InputRow,
  SelectRow,
  SwitchRow,
} from 'app/components/commons/CustomFields/CustomFields';
import { RestrictedPackSelect } from 'app/components/commons/RestrictedPackSelect/RestrictedPackSelect';
import { FormSection } from 'app/components/forms/FormSection';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useCreateVoucher } from 'app/hooks/data/useVoucher';
import { useAppSelector } from 'app/redux/hooks';
import { selectCountries } from 'app/redux/selectors/conf';
import type { VoucherForm, VoucherFormValues } from 'app/typings/voucher';
import { copyToClipboard } from 'app/utils/clipboard';
import { dayjs } from 'app/utils/dayjs';
import { splitPackSelectionValues } from 'app/utils/packSelection';
import { generateCode } from 'app/utils/strings';
import { parseInputNumber } from 'app/utils/typing';

import './Vouchers.scss';

const { Content } = Layout;

const DISCOUNT_PERCENT_WARNING_THRESHOLD = 20;

export const Vouchers = () => {
  const initialValues = {
    giftCardType: 'voucher',
    valueType: 'FIX',
    code: generateCode(7),
    minAmount: 0,
    startDate: dayjs(),
    expirationDate: dayjs().add(1, 'year'),
    restrictedToCountry: false,
  };
  const [form] = Form.useForm<VoucherFormValues>();

  const { mutateAsync: createVoucher } = useCreateVoucher({
    onSuccess: (_, variables) => {
      form.resetFields();

      if (variables?.code) {
        copyToClipboard(variables.code);
      }
    },
  });
  const { user } = useContext(UserContext);

  const countries = useAppSelector(selectCountries);
  const sortedCountries = countries.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const onSubmit = ({
    specificPackageOnly: restrictToPacks,
    restrictedPacks,
    ...values
  }: VoucherFormValues) => {
    if (!user) {
      return;
    }

    const { packageIds, packIds } = splitPackSelectionValues(
      restrictToPacks ? restrictedPacks : []
    );

    const formattedValues: VoucherForm =
      values.giftCardType === 'incentive'
        ? {
            ...values,
            restrictedPackageIds: packageIds,
            restrictedPackIds: packIds,
            userId: user.id,
          }
        : {
            ...values,
            startDate: dayjs(),
            maxUse: 1,
            onlyFirstBooking: false,
            userId: user.id,
            valueType: 'FIX',
          };

    createVoucher(formattedValues);
  };

  const reset = () => {
    form.resetFields();
    form.setFieldValue('code', generateCode(7));
  };

  const isGiftCardTypeIncentive =
    Form.useWatch('giftCardType', form) === 'incentive';
  const valueType = Form.useWatch('valueType', form) ?? initialValues.valueType;
  const discount = Form.useWatch('discount', form);
  const isValueTypeFixed = valueType === 'FIX';
  const specificPackageOnly = Form.useWatch('specificPackageOnly', form);

  return (
    <Layout className="voucher-layout">
      <SDetailLayout
        title="New Voucher"
        mode="edit"
        isDirty
        headerExtra={[
          <Button type="primary" size="large" onClick={reset} key="reset">
            Reset
          </Button>,
        ]}
      >
        <Content>
          <Form
            form={form}
            initialValues={initialValues}
            onFinish={onSubmit}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            onValuesChange={(changedValues) => {
              if (changedValues.giftCardType === 'incentive') {
                form.setFieldsValue({
                  startDate: dayjs(),
                  expirationDate: dayjs().add(3, 'months'),
                  onlyFirstBooking: false,
                });
              } else if (changedValues.giftCardType) {
                form.setFieldsValue({
                  startDate: dayjs(),
                  expirationDate: dayjs().add(1, 'year'),
                  valueType: 'FIX',
                });
              }
            }}
          >
            <div className="page-content">
              <FormSection title="Type" bordered>
                <div className="sectionSpacing">
                  <SelectRow
                    label="Type"
                    name="giftCardType"
                    dataSource={[
                      { id: 'voucher', name: 'Voucher' },
                      { id: 'incentive', name: 'Incentive' },
                      { id: 'internal', name: 'Internal' },
                      { id: 'manual', name: 'Manual Gift Card' },
                    ]}
                    required
                    noStyle
                  />
                </div>
              </FormSection>
              <FormSection title="Use conditions">
                <div className="sectionSpacing">
                  {isGiftCardTypeIncentive && (
                    <DatePickerRow
                      label="Start date"
                      name="startDate"
                      format="dddd DD MMMM YYYY"
                      required
                      noStyle
                    />
                  )}
                  <DatePickerRow
                    label="Expiration date"
                    name="expirationDate"
                    format="dddd DD MMMM YYYY"
                    required
                    noStyle
                  />
                  <SelectRow
                    label="Country"
                    name="countryId"
                    dataSource={sortedCountries}
                    placeholder="France"
                    required
                    noStyle
                  />
                  <div className="restrictedCountryRow">
                    <Form.Item
                      name="restrictedToCountry"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox />
                    </Form.Item>
                    <span>
                      Limit the use of the voucher only to the country to which
                      it is attached.
                    </span>
                  </div>
                  {isGiftCardTypeIncentive && (
                    <>
                      <InputNumberRow
                        label="Maximum code use"
                        name="maxUse"
                        min={1}
                        required
                        noStyle
                      />
                      <SwitchRow
                        label="1st booking only"
                        name="onlyFirstBooking"
                        noStyle
                      />
                    </>
                  )}
                </div>
              </FormSection>
              <FormSection title="Value">
                <div className="sectionSpacing">
                  {isGiftCardTypeIncentive && (
                    <CustomRow label="Value type" name="valueType" noStyle>
                      <Radio.Group>
                        <Radio value="FIX">Amount</Radio>
                        <Radio value="PERCENT">Percent</Radio>
                      </Radio.Group>
                    </CustomRow>
                  )}

                  {isValueTypeFixed ? (
                    <CustomRow label="Value" name="value" required noStyle>
                      <CurrencyInput
                        min={0}
                        currency="€"
                        parser={parseInputNumber}
                      />
                    </CustomRow>
                  ) : (
                    <CustomRow
                      label="Value"
                      name="discount"
                      required
                      noStyle
                      formItemNotice={
                        discount > DISCOUNT_PERCENT_WARNING_THRESHOLD && (
                          <span className="textError">
                            <InfoCircleOutlined className="infoIcon" />
                            Discount higher than 20% should be exceptional, make
                            sure that is what you want.
                          </span>
                        )
                      }
                    >
                      <CurrencyInput
                        min={0}
                        max={100}
                        currency="%"
                        parser={parseInputNumber}
                      />
                    </CustomRow>
                  )}

                  <CustomRow
                    label="Minimum use amount"
                    name="minAmount"
                    required
                    noStyle
                  >
                    <CurrencyInput
                      min={0}
                      currency="€"
                      parser={parseInputNumber}
                    />
                  </CustomRow>
                </div>
              </FormSection>

              {isGiftCardTypeIncentive && (
                <FormSection title="Package" bordered>
                  <div className="sectionSpacing">
                    <SwitchRow
                      label="Specific package only"
                      name="specificPackageOnly"
                      noStyle
                    />
                    {specificPackageOnly && (
                      <RestrictedPackSelect name="restrictedPacks" />
                    )}
                  </div>
                </FormSection>
              )}
              <FormSection title="Code">
                <div className="sectionSpacing">
                  <InputRow
                    label="Beneficiary"
                    name="beneficiary"
                    placeholder="Kevin Hutchings"
                    required
                    noStyle
                  />
                  <InputRow
                    label="Code"
                    name="code"
                    placeholder="ABCDEF"
                    required
                    noStyle
                  />
                </div>
              </FormSection>
            </div>
          </Form>
        </Content>
        <div className="sticky-footer">
          <Button type="primary" size="large" onClick={form.submit}>
            Save
          </Button>
        </div>
      </SDetailLayout>
    </Layout>
  );
};
